import _ from 'lodash';
import {
  TDateRanges,
  TDeltas,
  TFilter,
  TFilterItem,
  TIdentifiers,
  TProperties,
  TStopType,
} from 'utils/context/FilterContext/types';
import { getKeyByValue, isEmpty } from 'utils/context/FilterContext/utils';
import DK, { OrderTags } from 'utils/dictionaries/filter_dictionary';

import {
  IShipmentSearchCriteria,
  ShipmentIdentifierFilter,
} from '@project44-models/shipment-list';

function mapCheckbox(checkboxes: string[], key: string) {
  if (checkboxes?.length) {
    return [
      {
        [key]: checkboxes.map((type) => getKeyByValue(DK, type)),
        operator: 'EQUALS',
      },
    ];
  }
}

function mapTransportModes(modes: string[], scopes?: string[]) {
  if (modes?.length) {
    return [
      {
        mode: modes.map((type) => getKeyByValue(DK, type)),
        operator: 'EQUALS',
        ...(scopes && { scopes }),
      },
    ];
  }
}

function mapTrackingCheckbox(checkboxes: string[], key: string) {
  if (checkboxes?.length) {
    return [
      {
        scopes: ['LATEST'],
        [key]: checkboxes.map((type) => getKeyByValue(DK, type)),
        operator: 'EQUALS',
      },
    ];
  }
}

function mapDateEvent(
  dateEvent: TFilterItem[] = [['', '', ['']]],
  key: string = '',
) {
  const dateRanges: TDateRanges = [];
  const deltas: TDeltas = [];
  const stopProperties: TProperties = [];
  const stopIdentifiers: TIdentifiers = [];
  let stopType: TStopType;

  if (!Array.isArray(dateEvent)) {
    return null;
  }

  dateEvent
    .filter((type) => type.length === 3 && type[2][0])
    .forEach(([criteria, operator, terms]) => {
      if (
        [
          DK.EVENT_LOCATION_NAME,
          DK.EVENT_ADDRESS,
          DK.EVENT_CITY,
          DK.EVENT_STATE,
          DK.EVENT_COUNTRY,
          DK.EVENT_ZIP,
        ].includes(criteria)
      ) {
        const field = getKeyByValue(DK, criteria) || '';
        return stopProperties.push({
          propertyName: field
            .replace('LOCATION_', '')
            .replace('EVENT_', 'LOCATION_'),
          values: terms,
          operator: getKeyByValue(DK, operator),
        });
      }

      if (DK.EVENT_PORT_CODE === criteria) {
        return stopIdentifiers.push({
          types: ['PORT_UN_LOCODE'],
          values: terms,
          operator: getKeyByValue(DK, operator),
        });
      }

      if (DK.EVENT_AIRPORT_CODE === criteria) {
        return stopIdentifiers.push({
          types: ['AIRPORT_IATA', 'AIRPORT_ICAO'],
          values: terms,
          operator: getKeyByValue(DK, operator),
        });
      }

      if (DK.EVENT_STOP_TYPE === criteria) {
        stopType = {
          types: terms,
          operator: getKeyByValue(DK, operator),
        };
        return stopType;
      }

      if ([DK.RUNNING_EARLY, DK.RUNNING_LATE].includes(operator)) {
        const didOccur =
          criteria === DK.ACTUAL_DATETIME ? 'OCCURRED' : 'RUNNING';
        const isEarly = operator === DK.RUNNING_EARLY ? 'EARLY' : 'LATE';
        return deltas.push({
          type: [didOccur, isEarly].join('_'),
          value: terms.reduce((prev, curr) => prev * parseInt(curr, 10), 1),
          operator: 'MORE_THAN',
        });
      }

      if ([DK.IN_THE_LAST, DK.IN_THE_NEXT].includes(operator)) {
        const minutes = terms.reduce(
          (prev, curr) => prev * parseInt(curr, 10),
          1,
        );
        const isNext = operator === DK.IN_THE_NEXT;
        return dateRanges.push({
          type: getKeyByValue(DK, criteria),
          offsetMinutes: isNext ? minutes : -minutes,
          operator: 'WITHIN',
        });
      }

      dateRanges.push({
        type: getKeyByValue(DK, criteria),
        startDateTime: new Date(parseInt(terms[0], 10)),
        endDateTime: new Date(parseInt(terms[1], 10)),
        operator: getKeyByValue(DK, operator),
      });
    });

  if (
    dateRanges.length ||
    deltas.length ||
    stopIdentifiers.length ||
    stopProperties.length ||
    stopType
  ) {
    return {
      type: key,
      dateRanges,
      deltas,
      stopIdentifiers,
      stopProperties,
      stopType,
    };
  }
}

function mapIdentifierTypes(identifier: TFilterItem[] = []) {
  return identifier
    .filter((type) => type.length === 3 && type[2][0])
    .map(([criteria, operator, terms]) => {
      const key = getKeyByValue(DK, criteria) || '';
      return {
        types: [key.replace(/ORDER_(TAG|IDENTIFIER)_/, '')],
        operator: getKeyByValue(DK, operator),
        values: terms,
      };
    });
}

function mapOrderDates(identifier: TFilterItem[] = []) {
  const orderDateRanges: TDateRanges = [];
  identifier
    .filter((date) => date.length === 3 && date[2][0])
    .forEach((date) => {
      if ([DK.IN_THE_LAST, DK.IN_THE_NEXT].includes(date[1])) {
        const minutes = parseInt(date[2][0]) * parseInt(date[2][1]);
        const isNext = date[1] === DK.IN_THE_NEXT;
        orderDateRanges.push({
          type: getKeyByValue(DK, date[0]),
          offsetMinutes: isNext ? minutes : -minutes,
          operator: 'WITHIN',
        });
      } else {
        orderDateRanges.push({
          type: getKeyByValue(DK, date[0]),
          startDateTime: new Date(parseInt(date[2][0], 10)),
          endDateTime: new Date(parseInt(date[2][1], 10)),
          operator: getKeyByValue(DK, date[1]),
        });
      }
    });
  return orderDateRanges;
}

function mapIdentifierValues(identifier: TFilterItem[] = []) {
  return identifier
    .filter((type) => type.length === 3 && type[2][0])
    .map(
      ([criteria, operator, terms]) =>
        criteria && {
          operator: getKeyByValue(DK, operator),
          values: terms,
        },
    );
}

function mapIdentifier(identifier: TFilterItem[] = [], key: string = '') {
  return identifier
    .filter((type) => type.length === 3 && type[2][0])
    .map(([criteria, operator, terms]) => ({
      [key]: (key === 'type' && getKeyByValue(DK, criteria)) || criteria,
      operator: getKeyByValue(DK, operator),
      values: terms,
    }));
}

function mapPropertyIdentifier(identifier: TFilterItem[] = []) {
  return identifier
    .filter((type) => type.length === 3 && type[2][0])
    .map(([criteria, operator, terms]) => ({
      types: [getKeyByValue(DK, criteria) || criteria],
      operator: getKeyByValue(DK, operator),
      values: terms,
    }));
}

const mapSharingContext = (identifier: TFilterItem[] = []) => {
  return identifier
    .filter((type) => type.length === 3 && type[2][0])
    .map(([criteria, operator, terms]) => {
      const key = getKeyByValue(DK, criteria) || '';
      const scope = key.includes('RECIPIENT') ? 'RECIPIENT' : 'ORIGINATOR';
      return {
        scopes: [scope],
        operator: getKeyByValue(DK, operator),
        names: terms,
      };
    });
};

function mapType(types: string[], operator: string, values: string[]) {
  return {
    types,
    values,
    operator: getKeyByValue(DK, operator),
  };
}

const overrides = {
  [DK.DESTINATION_ADDRESS]: DK.LOCATION_ADDRESS,
  [DK.DESTINATION_AIRPORT_CODE]: DK.LOCATION_AIRPORT_CODE,
  [DK.DESTINATION_CITY]: DK.LOCATION_CITY,
  [DK.DESTINATION_COUNTRY]: DK.LOCATION_COUNTRY,
  [DK.DESTINATION_LOCATION_NAME]: DK.LOCATION_NAME,
  [DK.DESTINATION_PORT_CODE]: DK.LOCATION_PORT_CODE,
  [DK.DESTINATION_STATE]: DK.LOCATION_STATE,
  [DK.DESTINATION_ZIP]: DK.LOCATION_ZIP,

  [DK.ORIGIN_ADDRESS]: DK.LOCATION_ADDRESS,
  [DK.ORIGIN_AIRPORT_CODE]: DK.LOCATION_AIRPORT_CODE,
  [DK.ORIGIN_CITY]: DK.LOCATION_CITY,
  [DK.ORIGIN_COUNTRY]: DK.LOCATION_COUNTRY,
  [DK.ORIGIN_LOCATION_NAME]: DK.LOCATION_NAME,
  [DK.ORIGIN_PORT_CODE]: DK.LOCATION_PORT_CODE,
  [DK.ORIGIN_STATE]: DK.LOCATION_STATE,
  [DK.ORIGIN_ZIP]: DK.LOCATION_ZIP,

  [DK.STOP_ADDRESS]: DK.LOCATION_ADDRESS,
  [DK.STOP_AIRPORT_CODE]: DK.LOCATION_AIRPORT_CODE,
  [DK.STOP_CITY]: DK.LOCATION_CITY,
  [DK.STOP_COUNTRY]: DK.LOCATION_COUNTRY,
  [DK.STOP_LOCATION_NAME]: DK.LOCATION_NAME,
  [DK.STOP_PORT_CODE]: DK.LOCATION_PORT_CODE,
  [DK.STOP_STATE]: DK.LOCATION_STATE,
  [DK.STOP_ZIP]: DK.LOCATION_ZIP,
};

function mapLocation(scopes: string[], locations: TFilterItem[] = []) {
  const identifiers: TIdentifiers = [];
  const properties: TProperties = [];
  let stopType: TStopType;

  locations.forEach(([criteria, operator, terms]) => {
    if (!terms[0]) {
      return;
    }

    let crit = criteria;
    if (Object.keys(overrides).includes(criteria)) {
      crit = overrides[criteria];
    }

    switch (crit) {
      case DK.LOCATION_AIRPORT_CODE: {
        identifiers.push(
          mapType(['AIRPORT_IATA', 'AIRPORT_ICAO'], operator, terms),
        );
        break;
      }
      case DK.LOCATION_PORT_CODE:
        identifiers.push(mapType(['PORT_UN_LOCODE'], operator, terms));
        break;
      case DK.STOP_TYPE:
        stopType = {
          types: terms,
          operator: getKeyByValue(DK, operator),
        };
        break;
      default:
        properties.push({
          propertyName: getKeyByValue(DK, crit),
          values: terms,
          operator: getKeyByValue(DK, operator),
        });
    }
  });

  return {
    identifiers,
    properties,
    stopType,
    scopes,
  };
}

const mapSort = ([sortBy, direction]: string[] = []) => {
  if (sortBy && direction) {
    return [
      {
        fieldName: getKeyByValue(DK, sortBy),
        direction: getKeyByValue(DK, direction),
      },
    ];
  }
};

function removeEmptyPropsFromArray<TData extends object>(collection: TData[]) {
  return _.map(collection, (obj) => _.omitBy(obj, isEmpty));
}

type IBulkIdentifier = [type: string, operator: string, values: string[]];

function mapBulkIdentifiers(identifiers: IBulkIdentifier[] = []) {
  return identifiers.map(([type, operator, values]) =>
    mapType([getKeyByValue(DK, type)], operator, values),
  );
}

export const getShipmentsRequest = (
  filters: TFilter,
): IShipmentSearchCriteria => {
  const searchText = filters[DK.SEARCH_TEXT] as string;
  const sortBy = mapSort(filters[DK.SORT_BY] as string[]);
  const connect = filters[DK.CONNECTIVITY] as string[];
  const currentState = filters[DK.CURRENT_STATE] as string[];
  const idType = filters[DK.ID_TYPE] as string[];
  const identifiers = filters[DK.IDENTIFIER] as IBulkIdentifier[];

  const bulkShipmentIdentifiers = mapBulkIdentifiers(identifiers);

  const shipmentTrackingStatusEvents = mapTrackingCheckbox(connect, 'types');
  const shipmentIdentifiers = (mapCheckbox(idType, 'types') ?? []).concat(
    bulkShipmentIdentifiers,
  );

  const shipmentStates = mapCheckbox(currentState, 'types');

  const loadItem = filters[DK.LOAD_ITEM] as TFilterItem[];
  const loadItemIdentifiers = mapIdentifierTypes(
    loadItem?.filter((type) => !DK.LOAD_ITEM_DESCRIPTION.includes(type[0])),
  );
  const loadItemDescriptions = mapIdentifierValues(
    loadItem?.filter((type) => DK.LOAD_ITEM_DESCRIPTION.includes(type[0])),
  );

  const orderRefKey = filters[DK.ORDER_TAGS] as TFilterItem[];
  const orderAdditionalIdentifiers = mapIdentifierTypes(
    orderRefKey?.filter(([name]) => !Object.values(OrderTags).includes(name)),
  );
  const orderTags = mapIdentifierTypes(
    orderRefKey?.filter(([name]) => Object.values(OrderTags).includes(name)),
  );

  const orderDate = filters[DK.ORDER_DATE] as TFilterItem[];
  const orderIdentifier = filters[DK.ORDER_IDENTIFIER] as TFilterItem[];
  const orderDateRanges = mapOrderDates(orderDate);
  const orderIdentifiers = mapIdentifier(orderIdentifier, 'type');

  const transportationModes = mapTransportModes(
    filters[DK.MODE_OF_SHIPMENT] as string[],
    filters[DK.MODE_OF_SHIPMENT_SCOPES] as string[],
  );

  const referenceKey = filters[DK.REFERENCE_KEY] as TFilterItem[];
  const carrier = filters[DK.CARRIER] as TFilterItem[];
  const vesselId = filters[DK.VESSEL_OR_VEHICLE_ID] as TFilterItem[];
  const shipmentAttributes = mapIdentifier(referenceKey, 'name');
  const shipmentCarrierIdentifiers = mapPropertyIdentifier(carrier);
  const shipmentVesselIdentifiers = mapPropertyIdentifier(vesselId);

  const sharingContextTenants = mapSharingContext(
    filters[DK.SHARED_VISIBILITY] as TFilterItem[],
  );

  const shipmentStops = [
    mapLocation(['DESTINATION'], filters[DK.DESTINATION] as TFilterItem[]),
    mapLocation(['ORIGIN'], filters[DK.ORIGIN] as TFilterItem[]),
    mapLocation(['ANY_STOP'], filters[DK.ANY_STOP] as TFilterItem[]),
    mapLocation(['EVERY_STOP'], filters[DK.ALL_STOP] as TFilterItem[]),
    mapLocation(['CURRENT'], filters[DK.CURRENT_STOP] as TFilterItem[]),
  ].filter(
    (stop) =>
      stop.identifiers.length || stop.properties.length || !!stop.stopType,
  );

  const shipmentEvents = [
    'ARRIVAL_AT_STOP',
    'AVAILABLE',
    'CONSIGNEE_NOTIFY',
    'DELIVERY',
    'DEPARTURE_FROM_STOP',
    'DISCHARGE',
    'DISPATCH',
    'FLIGHT_BOOKING',
    'GATE_IN_EMPTY',
    'GATE_IN_FULL',
    'GATE_OUT_EMPTY',
    'GATE_OUT_FULL',
    'LOAD',
    'LOAD_ONTO_VEHICLE',
    'MANIFEST',
    'OUT_FOR_DELIVERY',
    'PICKED_UP',
    'RECEIVE_FROM_SHIPPER',
    'RETURN_TO_SENDER',
    'UNLOAD_FROM_VEHICLE',
    'LAST_FREE_DAY',
  ]
    .map((key) => mapDateEvent(filters[DK[key]] as TFilterItem[], key))
    .filter(Boolean);

  const request: IShipmentSearchCriteria = {
    searchText,
    sort: sortBy,
    // Checkboxes
    shipmentTrackingStatusEvents,
    shipmentIdentifiers,
    shipmentStates,
    transportationModes,
    // Events
    shipmentEvents,
    // Identifiers
    loadItemIdentifiers,
    loadItemDescriptions,
    orderAdditionalIdentifiers,
    orderTags,
    orderIdentifiers,
    orderDateRanges,
    sharingContextTenants,
    shipmentAttributes,
    shipmentCarrierIdentifiers,
    shipmentVesselIdentifiers,
    // Locations
    shipmentStops,
    // TODO fix generics issue with util functions
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any;

  if (request.shipmentStops?.length) {
    request.shipmentStops = removeEmptyPropsFromArray(request.shipmentStops);
  }

  if (request.shipmentEvents?.length) {
    request.shipmentEvents = removeEmptyPropsFromArray(
      request.shipmentEvents,
    ) as IShipmentSearchCriteria['shipmentEvents'];
  }

  if (request.shipmentIdentifiers?.length) {
    const bulkIdentifiers = request.shipmentIdentifiers.filter(
      (id: ShipmentIdentifierFilter) =>
        !!id.values && !_.isEqual(id.values, ['']),
    );

    const specificIdenfiers = request.shipmentIdentifiers.filter(
      (id: ShipmentIdentifierFilter) => !id.values,
    );

    request.shipmentIdentifiers = specificIdenfiers.concat(bulkIdentifiers);
  }

  return _.omitBy(request, isEmpty);
};
